import {
  defaultLocaleCode,
  getLocaleFromPath,
  nativeLocalizedPaths,
  normalizePath,
  stripLocaleFromPath,
} from '~/data/locales';
import { enHomeCopy } from '~/data/localized-copy';
import { FOUNDING_OFFER, FOUNDING_OFFER_DICTIONARY_KEYS } from '~/data/founding-offer';

type Dictionary = Record<string, string>;

// Natively localized routes (/ and /pricing) translate their English copy through
// one dictionary per locale in data/home-i18n/ (English text → translation). Each
// locale is its own lazy chunk, and the loaded dictionary rides in the page payload,
// so a page ships only its own language. Other routes get null and keep English,
// which the generated-editions pipeline translates. `pnpm i18n:home` lists gaps.
const dictionaries = import.meta.glob<Dictionary>('../data/home-i18n/*.json', {
  import: 'default',
});

export const homeDictionaryLocale = (path: string) => {
  const locale = getLocaleFromPath(path);
  if (locale.code === defaultLocaleCode) return '';
  return nativeLocalizedPaths.has(normalizePath(stripLocaleFromPath(path))) ? locale.code : '';
};

// After the Founding 100 deadline the offer copy no longer renders, but the loaded dictionary
// still rides in the page payload (__NUXT_DATA__). A build without the offer drops those entries
// (FOUNDING_OFFER_DICTIONARY_KEYS), so the 52 localized / and /pricing editions carry none of them.
const FOUNDING_ENTRIES = new Set(FOUNDING_OFFER_DICTIONARY_KEYS);

export const loadHomeDictionary = async (code: string) => {
  const load = dictionaries[`../data/home-i18n/${code}.json`];
  const dict = load ? await load() : null;
  if (!dict || FOUNDING_OFFER.enabled) return dict;
  return Object.fromEntries(Object.entries(dict).filter(([english]) => !FOUNDING_ENTRIES.has(english)));
};

export const useHomeDictionary = () =>
  useState<{ code: string; dict: Dictionary | null }>('home-i18n', () => ({
    code: '',
    dict: null,
  }));

const canonical = (text: string) => text.replace(/\s+/g, ' ').trim();

const translateDeep = <T>(value: T, dict: Dictionary): T => {
  if (typeof value === 'string') return (dict[canonical(value)] ?? value) as T;
  if (Array.isArray(value)) return value.map((item) => translateDeep(item, dict)) as T;
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [key, translateDeep(item, dict)]),
    ) as T;
  }
  return value;
};

export const useHomeI18n = () => {
  const state = useHomeDictionary();
  const translated = computed(() => Boolean(state.value.dict));
  const tr = (text: string) => state.value.dict?.[canonical(text)] ?? text;
  return { tr, translated };
};

// Home copy for the current route: the full English copy run through the locale's
// dictionary where one is loaded, otherwise the shared localized copy.
export const useHomeCopy = () => {
  const { homeCopy } = useWorkspaceLocale();
  const state = useHomeDictionary();
  return computed(() =>
    state.value.dict ? translateDeep(enHomeCopy, state.value.dict) : homeCopy.value,
  );
};
